import { ElementType, useEffect, useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Button, Popover, SxProps, Theme, Typography } from '@mui/material';

const selectBaseStyles: SxProps<Theme> = {
  height: '34px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  backgroundColor: (theme) => theme.palette.background.paper,
  '&:hover': {
    borderColor: (theme) => theme.palette.text.primary,
  },
  paddingRight: '5px',
  paddingLeft: '12px',
};

const customStyles: Record<string, SxProps<Theme>> = {
  container: {
    position: 'relative',
    width: '100%',
  },
  select: {
    ...selectBaseStyles,
    border: (theme) => `1px solid #C4C4C4`,
  },
  openSelect: {
    ...selectBaseStyles,
    border: (theme) => `2px solid ${theme.palette.primary.main}`,
  },
  value: {
    color: (theme) => theme.palette.text.primary,
    fontSize: '14px',
  },
  popover: {
    marginTop: '4px',
    '& .MuiPopover-paper': {
      borderRadius: '8px',
      boxShadow: (theme) => theme.shadows[3],
      padding: '0',
      width: '120px',
      border: (theme) => `1px solid ${theme.palette.divider}`,
    },
  },
  columnsContainer: {
    display: 'flex',
    gap: 0,
    marginBottom: 0,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    width: '70px',
    maxHeight: '200px',
    overflowY: 'auto',
    '&:first-of-type': {
      borderRight: (theme) => `1px solid ${theme.palette.divider}`,
    },
  },
  option: {
    padding: '8px 12px',
    cursor: 'pointer',
    textAlign: 'center',
    fontSize: '14px',
    color: (theme) => theme.palette.text.primary,
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
  },
  selectedOption: {
    padding: '8px 12px',
    cursor: 'pointer',
    textAlign: 'center',
    fontSize: '14px',
    color: (theme) => theme.palette.text.primary,
    backgroundColor: 'rgba(25, 118, 210, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.16)',
    },
  },
  okTextButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '12px 16px',
    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
  },
  okTextButton: {
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: (theme) => theme.palette.primary.main,
    padding: '4px 8px',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      color: (theme) => theme.palette.primary.dark,
    },
  },
  icon: {
    color: (theme) => theme.palette.action.active,
    fontSize: '25px',
    paddingRight: 0,
  },
};

// Generate hours (00-23)
const HOURS = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, '0'));

// Generate minutes (00, 05, 10, ..., 55)
const MINUTES = Array.from({ length: 12 }, (_, index) => (index * 5).toString().padStart(2, '0'));

interface CustomTimePickerProps {
  /** When true, every hour or minute change fires `onChange` automatically (no
   *  "OK" click needed). Useful for forms where the explicit confirmation adds
   *  friction. Defaults to `false` to keep FormBuilder's previous (legacy)
   *  behaviour. */
  autoCommit?: boolean;
  value: string;
  onChange: (value: string) => void;
  /** Style overrides for the visible select Box (e.g. height, borderRadius). */
  sx?: SxProps<Theme>;
  /** Overrides that apply only to the closed state, not to open/focus. Useful
   *  to change the normal border colour without overriding the focus border. */
  closedSx?: SxProps<Theme>;
  /** Dropdown icon. Defaults to ArrowDropDownIcon (legacy behaviour). */
  IconComponent?: ElementType;
  /** Style overrides for the dropdown icon. */
  iconSx?: SxProps<Theme>;
}

export function CustomTimePicker({
  value,
  onChange,
  sx,
  closedSx,
  IconComponent,
  iconSx,
  autoCommit = false,
}: CustomTimePickerProps) {
  const DropdownIcon = IconComponent ?? ArrowDropDownIcon;
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const [initialHour, initialMinute] = value ? value.split(':') : ['00', '00'];
  const [selectedHour, setSelectedHour] = useState<string>(initialHour);
  const [selectedMinute, setSelectedMinute] = useState<string>(initialMinute);

  const hourColumnRef = useRef<HTMLDivElement>(null);
  const minuteColumnRef = useRef<HTMLDivElement>(null);

  const open = Boolean(popoverAnchor);

  // Scroll to selected option when opening
  // setTimeout is needed to ensure DOM is fully rendered before scrolling
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (hourColumnRef.current) {
          const selectedElement = hourColumnRef.current.querySelector(
            `[data-value="${selectedHour}"]`,
          ) as HTMLElement;
          if (selectedElement) {
            selectedElement.scrollIntoView({ block: 'center' });
          }
        }
        if (minuteColumnRef.current) {
          const selectedElement = minuteColumnRef.current.querySelector(
            `[data-value="${selectedMinute}"]`,
          ) as HTMLElement;
          if (selectedElement) {
            selectedElement.scrollIntoView({ block: 'center' });
          }
        }
      }, 0);
    }
  }, [open, selectedHour, selectedMinute]);

  const handleConfirmTimeSelection = () => {
    const newValue = `${selectedHour}:${selectedMinute}`;
    onChange(newValue);
    setPopoverAnchor(null);
  };

  return (
    <Box sx={customStyles.container}>
      <Box
        sx={[
          open ? customStyles.openSelect : customStyles.select,
          ...(!open && closedSx ? [closedSx] : []),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={(event) => setPopoverAnchor(event.currentTarget)}
      >
        <Typography sx={customStyles.value}>{value}</Typography>
        <DropdownIcon sx={iconSx ? [customStyles.icon, iconSx] : customStyles.icon} />
      </Box>
      <Popover
        open={open}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={customStyles.popover}
      >
        <Box sx={customStyles.columnsContainer}>
          <Box
            ref={hourColumnRef}
            sx={customStyles.column}
          >
            {HOURS.map((hour) => (
              <Box
                key={hour}
                data-value={hour}
                sx={hour === selectedHour ? customStyles.selectedOption : customStyles.option}
                onClick={() => {
                  setSelectedHour(hour);
                  if (autoCommit) onChange(`${hour}:${selectedMinute}`);
                }}
              >
                {hour}
              </Box>
            ))}
          </Box>
          <Box
            ref={minuteColumnRef}
            sx={customStyles.column}
          >
            {MINUTES.map((minute) => (
              <Box
                key={minute}
                data-value={minute}
                sx={minute === selectedMinute ? customStyles.selectedOption : customStyles.option}
                onClick={() => {
                  setSelectedMinute(minute);
                  if (autoCommit) onChange(`${selectedHour}:${minute}`);
                }}
              >
                {minute}
              </Box>
            ))}
          </Box>
        </Box>
        {!autoCommit && (
          <Box sx={customStyles.okTextButtonContainer}>
            <Button
              variant='text'
              sx={customStyles.okTextButton}
              onClick={handleConfirmTimeSelection}
            >
              OK
            </Button>
          </Box>
        )}
      </Popover>
    </Box>
  );
}
